<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { vOnClickOutside } from '@vueuse/components'
import { mdiHomeCircle } from '@mdi/js'
import type { Client } from '../models/client.models'

import BaseButton from '@/shared/components/button/BaseButton.vue'
import CaretIcon from '@/shared/components/icon/CaretIcon.vue'
import SvgIcon from '@/shared/components/icon/SvgIcon.vue'
import FadeTransition from '@/shared/components/transitions/FadeTransition.vue'

interface Props {
  clients: Client[]
}

interface Emits {
  (e: 'change'): void
}

const emits = defineEmits<Emits>()
const model = defineModel<Client | undefined>({ required: true })
const props = defineProps<Props>()

const filter = ref<string>('')
const isActive = ref(false)

const loadDefault = () => {
  if (model.value) {
    const client = props.clients.find(
      (client: Client) => client.clientKey === model.value?.clientKey,
    )
    filter.value = client ? client.clientName : ''
  }
}

const filteredOptions = computed((): Client[] => {
  return filter.value === ''
    ? props.clients || []
    : props.clients?.filter(
        (client) =>
          client.clientName && client.clientName.toLowerCase().includes(filter.value.toLowerCase()),
      ) || []
})

const updateModel = (client: Client) => {
  filter.value = client.clientName
  model.value = client
  isActive.value = false
  emits('change')
}

const closeMenu = () => {
  isActive.value = false
  loadDefault()
}

const toggleMenu = () => {
  isActive.value = !isActive.value
  if (isActive.value) {
    filter.value = ''
  } else {
    loadDefault()
  }
}

const setValues = (event: Event) => {
  filter.value = (event.target as HTMLInputElement).value
  isActive.value = true
}

watch(model, () => loadDefault(), { immediate: true })
</script>

<template>
  <div class="relative inline-block" v-on-click-outside="() => closeMenu()" v-bind="$attrs">
    <div
      class="flex items-center cursor-pointer border-l border-gray-800 gap-2 px-2"
      @click="toggleMenu"
    >
      <SvgIcon class="fill-white dark:fill-brand h-5 w-5 shrink-0" :path="mdiHomeCircle" />
      <input
        type="text"
        autocomplete="off"
        spellcheck="false"
        class="flex-1 text-sm text-white outline-none bg-transparent py-1 truncate"
        :name="$t('glossary.client')"
        :title="model?.clientName"
        :value="filter"
        :disabled="clients.length === 1"
        @input="setValues"
      />
      <BaseButton class="shrink-0" :title="$t('common.open')" @click.stop="toggleMenu">
        <CaretIcon v-if="clients.length !== 1" :active="isActive" class="w-5 h-5" />
      </BaseButton>
    </div>
    <FadeTransition>
      <div
        v-if="isActive"
        class="z-40 absolute mt-1.5 border-x border-b border-gray-300 bg-white dark:border-gray-800 dark:bg-black min-w-full overflow-x-hidden max-h-63 focus:outline-none shadow-md"
      >
        <ul>
          <li
            v-for="(client, index) in filteredOptions"
            :key="index"
            class="flex cursor-pointer items-center text-nowra truncate text-sm dark:text-gray-300 hover:bg-gray-100 dark:hover:text-white dark:hover:bg-gray-800 p-2"
            @click="updateModel(client)"
          >
            <SvgIcon class="fill-white dark:fill-brand h-5 w-5 me-1.5" :path="mdiHomeCircle" />
            {{ client.clientName }}
          </li>
        </ul>
        <div v-if="filteredOptions?.length === 0" class="p-2 font-light text-sm dark:text-gray-400">
          {{ $t('message.noResult') }}
        </div>
      </div>
    </FadeTransition>
  </div>
</template>
